/**
 * Email helpers — enqueue into `email_queue`, drained by the
 * `process-email-queue` Edge Function (pg_cron, every 2 min) with retries.
 *
 * Enqueuing is a single fast DB insert, so callers (e.g. booking creation)
 * never block on — or fail because of — a Resend outage.
 *
 * All templates use inline CSS (required for Gmail/Outlook compatibility).
 *
 * From address: Stratakey <onboarding@resend.dev>
 * (Switch to a custom domain via Resend dashboard when ready for production)
 */

import { supabase } from "@/lib/supabase";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SendEmailPayload {
  to: string;
  subject: string;
  html: string;
  replyTo?: string;
  bookingId?: string;
}

// ─── Core sender ──────────────────────────────────────────────────────────────

async function sendEmail(payload: SendEmailPayload): Promise<void> {
  const { error } = await supabase.from("email_queue").insert({
    recipient: payload.to,
    subject: payload.subject,
    html: payload.html,
    reply_to: payload.replyTo ?? null,
    booking_id: payload.bookingId ?? null,
  });
  if (error) {
    // Surface as a typed error — callers can decide whether to re-throw or warn
    throw new Error(`Failed to queue email: ${error.message}`);
  }
}

// ─── Shared template wrapper ─────────────────────────────────────────────────

/**
 * Wraps content in a branded, responsive email shell.
 * Uses a 600 px centered layout with an indigo header and a neutral footer.
 */
function emailShell(content: string, previewText = ""): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <title>Stratakey</title>
  <!--[if mso]>
  <noscript>
    <xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml>
  </noscript>
  <![endif]-->
</head>
<body style="margin:0;padding:0;background-color:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <!-- Preview text (hidden) -->
  ${previewText ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">${previewText}&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;</div>` : ""}

  <!-- Outer wrapper -->
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#f1f5f9;">
    <tr>
      <td style="padding:32px 16px;">
        <!-- Email card -->
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="max-width:600px;margin:0 auto;background-color:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.1);">

          <!-- Header -->
          <tr>
            <td style="background-color:#1e1b4b;padding:24px 32px;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td>
                    <!-- Logo mark -->
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0">
                      <tr>
                        <td style="background-color:#4f46e5;border-radius:8px;padding:8px 10px;vertical-align:middle;">
                          <span style="color:#ffffff;font-size:14px;font-weight:700;letter-spacing:0.5px;">K</span>
                        </td>
                        <td style="padding-left:10px;vertical-align:middle;">
                          <span style="color:#ffffff;font-size:18px;font-weight:700;letter-spacing:-0.3px;">Stratakey</span>
                          <div style="color:#a5b4fc;font-size:11px;margin-top:1px;letter-spacing:0.5px;text-transform:uppercase;">STR Compliance Platform</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Body content -->
          <tr>
            <td style="padding:40px 32px 32px;">
              ${content}
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color:#f8fafc;border-top:1px solid #e2e8f0;padding:20px 32px;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td>
                    <p style="margin:0;font-size:12px;color:#94a3b8;line-height:1.5;">
                      Stratakey STR Compliance Platform
                    </p>
                    <p style="margin:4px 0 0;font-size:11px;color:#cbd5e1;">
                      This email was sent because of activity on your Stratakey account.
                      If you believe you received this in error, please ignore it.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// ─── Shared components ────────────────────────────────────────────────────────

function ctaButton(text: string, href: string): string {
  return `
<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:24px 0;">
  <tr>
    <td style="background-color:#4f46e5;border-radius:8px;">
      <a href="${href}" target="_blank"
         style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;letter-spacing:-0.2px;">
        ${text} &rarr;
      </a>
    </td>
  </tr>
</table>`;
}

function detailsCard(rows: Array<{ label: string; value: string }>): string {
  const rowsHtml = rows
    .map(
      (r) => `
    <tr>
      <td style="padding:10px 0;border-bottom:1px solid #e2e8f0;font-size:14px;color:#64748b;width:40%;">${r.label}</td>
      <td style="padding:10px 0;border-bottom:1px solid #e2e8f0;font-size:14px;font-weight:600;color:#0f172a;">${r.value}</td>
    </tr>`
    )
    .join("");

  return `
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%"
       style="background-color:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:4px 16px;margin:20px 0;">
  <tbody>${rowsHtml}</tbody>
</table>`;
}

function infoBox(icon: string, text: string, color = "#fef3c7", borderColor = "#fde68a"): string {
  return `
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%"
       style="background-color:${color};border:1px solid ${borderColor};border-radius:8px;margin:20px 0;">
  <tr>
    <td style="padding:12px 16px;font-size:13px;color:#92400e;line-height:1.5;">
      ${icon}&nbsp; ${text}
    </td>
  </tr>
</table>`;
}

function divider(): string {
  return `<hr style="border:none;border-top:1px solid #e2e8f0;margin:24px 0;" />`;
}

// ─── Template 1: Invitation ───────────────────────────────────────────────────

interface InvitationEmailData {
  inviteeEmail: string;
  orgName: string;
  role: string;
  inviterName: string;
  acceptUrl: string;
  expiresAt: string; // ISO string
}

export async function sendInvitationEmail(data: InvitationEmailData): Promise<void> {
  const roleLabel = data.role.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const expiryDate = new Date(data.expiresAt).toLocaleDateString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const content = `
    <!-- Icon -->
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background-color:#ede9fe;border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
          <circle cx="9" cy="7" r="4" stroke="#4f46e5" stroke-width="2"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      You've been invited
    </h1>
    <p style="margin:0 0 24px;font-size:16px;color:#475569;text-align:center;">
      to join <strong style="color:#0f172a;">${data.orgName}</strong> on Stratakey
    </p>

    ${divider()}

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      <strong>${data.inviterName}</strong> has invited you to join the Stratakey STR compliance
      platform as a <strong>${roleLabel}</strong> for <strong>${data.orgName}</strong>.
    </p>

    <p style="margin:0 0 4px;font-size:15px;color:#334155;line-height:1.6;">
      As a ${roleLabel}, you'll be able to:
    </p>
    <ul style="margin:8px 0 20px;padding-left:20px;font-size:14px;color:#475569;line-height:1.8;">
      <li>View and manage STR host registrations</li>
      <li>Review and approve booking requests</li>
      <li>Monitor compliance across all units</li>
      <li>Access reports and audit logs</li>
    </ul>

    ${ctaButton("Accept Invitation", data.acceptUrl)}

    ${infoBox(
      "⏰",
      `This invitation expires on <strong>${expiryDate}</strong>. After that date, you'll need to request a new invitation.`,
      "#fef3c7",
      "#fde68a"
    )}

    ${divider()}

    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      If you weren't expecting this invitation, you can safely ignore this email.
      The invitation link will expire automatically and no action is required.
      <br/><br/>
      If you have any questions, please contact
      <a href="mailto:support@stratakey.com" style="color:#4f46e5;text-decoration:none;">support@stratakey.com</a>.
    </p>
  `;

  await sendEmail({
    to: data.inviteeEmail,
    subject: `You've been invited to join ${data.orgName} on Stratakey`,
    html: emailShell(content, `${data.inviterName} has invited you to join ${data.orgName} as a ${roleLabel}`),
  });
}

// ─── Template 2: Booking Confirmed (Guest) ────────────────────────────────────

interface BookingConfirmationEmailData {
  guestEmail: string;
  guestName: string;
  unitNumber: string;
  orgName: string;
  checkIn: string;   // YYYY-MM-DD
  checkOut: string;  // YYYY-MM-DD
  nights: number;
  guestCount: number;
  guestPortalUrl: string;
  hostName?: string;
  bookingId?: string;
}

export async function sendBookingConfirmationEmail(data: BookingConfirmationEmailData): Promise<void> {
  const fmt = (d: string) =>
    new Date(d + "T12:00:00").toLocaleDateString("en-CA", {
      weekday: "short",
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  const content = `
    <!-- Success icon -->
    <div style="text-align:center;margin-bottom:28px;">
      <div style="display:inline-block;background-color:#dcfce7;border-radius:50%;padding:16px;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style="display:block;">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#16a34a" stroke-width="2" stroke-linecap="round"/>
          <polyline points="22 4 12 14.01 9 11.01" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      Your stay is confirmed!
    </h1>
    <p style="margin:0 0 28px;font-size:16px;color:#475569;text-align:center;">
      ${data.orgName} &nbsp;·&nbsp; Unit ${data.unitNumber}
    </p>

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      Hi <strong>${data.guestName}</strong>,<br/>
      Great news! Your booking at <strong>Unit ${data.unitNumber}</strong>, ${data.orgName}
      has been approved and confirmed.
    </p>

    ${detailsCard([
      { label: "📅  Check-in",  value: fmt(data.checkIn)  },
      { label: "📅  Check-out", value: fmt(data.checkOut) },
      { label: "🌙  Duration",  value: `${data.nights} night${data.nights !== 1 ? "s" : ""}` },
      { label: "👥  Guests",    value: String(data.guestCount) },
      ...(data.hostName ? [{ label: "🏠  Host", value: data.hostName }] : []),
    ])}

    ${divider()}

    <h2 style="margin:0 0 12px;font-size:17px;font-weight:700;color:#0f172a;">Complete Your Online Check-In</h2>
    <p style="margin:0 0 4px;font-size:14px;color:#475569;line-height:1.6;">
      Before your arrival, please complete your online check-in by:
    </p>
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:12px 0;">
      <tr>
        <td style="padding:6px 0;font-size:14px;color:#334155;">
          <span style="display:inline-block;background-color:#dcfce7;color:#16a34a;font-weight:700;border-radius:50%;width:22px;height:22px;text-align:center;line-height:22px;font-size:12px;margin-right:10px;">✓</span>
          Acknowledge the building rules and strata bylaws
        </td>
      </tr>
      <tr>
        <td style="padding:6px 0;font-size:14px;color:#334155;">
          <span style="display:inline-block;background-color:#dcfce7;color:#16a34a;font-weight:700;border-radius:50%;width:22px;height:22px;text-align:center;line-height:22px;font-size:12px;margin-right:10px;">✓</span>
          Upload a photo of your government-issued photo ID
        </td>
      </tr>
    </table>

    ${ctaButton("Complete Guest Check-In", data.guestPortalUrl)}

    ${infoBox(
      "💡",
      "Keep this link handy — you can use it at any time before or during your stay to access your check-in portal.",
      "#eff6ff",
      "#bfdbfe"
    )}

    ${divider()}

    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      Questions about your stay? Reply to this email${data.hostName ? ` or contact your host, <strong>${data.hostName}</strong>` : ""}.
    </p>
  `;

  await sendEmail({
    to: data.guestEmail,
    subject: `Your stay at Unit ${data.unitNumber} is confirmed — ${fmt(data.checkIn)} to ${fmt(data.checkOut)}`,
    html: emailShell(
      content,
      `Your booking at Unit ${data.unitNumber}, ${data.orgName} has been confirmed for ${fmt(data.checkIn)}`
    ),
    bookingId: data.bookingId,
  });
}

// ─── Template 3: Booking Approved (Host notification) — type ─────────────────

interface BookingApprovedEmailData {
  hostEmail: string;
  hostName: string;
  guestName: string;
  guestEmail?: string;
  guestCount: number;
  unitNumber: string;
  orgName: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  dashboardUrl: string;
  bookingId?: string;
}

// ─── Template 4: Host Registration Status ────────────────────────────────────

interface HostRegistrationStatusEmailData {
  hostEmail: string;
  hostName: string;
  unitNumber: string;
  orgName: string;
  approved: boolean;
  reason?: string;
  dashboardUrl: string;
}

export async function sendHostRegistrationStatusEmail(data: HostRegistrationStatusEmailData): Promise<void> {
  const statusColor  = data.approved ? "#16a34a" : "#dc2626";
  const statusBg     = data.approved ? "#dcfce7" : "#fee2e2";
  const statusLabel  = data.approved ? "Approved ✓" : "Not Approved";

  const content = `
    <div style="text-align:center;margin-bottom:28px;">
      <div style="display:inline-block;background-color:${statusBg};border-radius:50%;padding:16px;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style="display:block;">
          ${data.approved
            ? `<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="${statusColor}" stroke-width="2" stroke-linecap="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="${statusColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
            : `<circle cx="12" cy="12" r="10" stroke="${statusColor}" stroke-width="2"/><line x1="15" y1="9" x2="9" y2="15" stroke="${statusColor}" stroke-width="2" stroke-linecap="round"/><line x1="9" y1="9" x2="15" y2="15" stroke="${statusColor}" stroke-width="2" stroke-linecap="round"/>`
          }
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      Registration ${statusLabel}
    </h1>
    <p style="margin:0 0 28px;font-size:15px;color:#475569;text-align:center;">
      STR Registration — Unit ${data.unitNumber} · ${data.orgName}
    </p>

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      Hi <strong>${data.hostName}</strong>,
    </p>
    <p style="margin:0 0 20px;font-size:15px;color:#334155;line-height:1.6;">
      ${data.approved
        ? `Your Short-Term Rental registration for <strong>Unit ${data.unitNumber}</strong> at <strong>${data.orgName}</strong> has been <strong style="color:${statusColor};">approved</strong>. You can now create bookings through the Stratakey platform.`
        : `Your Short-Term Rental registration for <strong>Unit ${data.unitNumber}</strong> at <strong>${data.orgName}</strong> has <strong style="color:${statusColor};">not been approved</strong> at this time.`
      }
    </p>

    ${!data.approved && data.reason ? `
    ${infoBox("ℹ️", `<strong>Reason:</strong> ${data.reason}`, "#fee2e2", "#fca5a5")}
    ` : ""}

    ${ctaButton(data.approved ? "Go to Dashboard" : "View Details", data.dashboardUrl)}

    ${divider()}

    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      If you have questions about your registration status, please contact your property manager.
    </p>
  `;

  await sendEmail({
    to: data.hostEmail,
    subject: `STR Registration ${statusLabel} — Unit ${data.unitNumber}, ${data.orgName}`,
    html: emailShell(content, `Your STR registration for Unit ${data.unitNumber} has been ${data.approved ? "approved" : "not approved"}`),
  });
}

// ─── Template 5: Registration Expiry Warning ──────────────────────────────────

interface RegistrationExpiryEmailData {
  hostEmail: string;
  hostName: string;
  unitNumber: string;
  orgName: string;
  daysUntilExpiry: number;
  expiryDate: string; // ISO
  renewalUrl: string;
}

export async function sendRegistrationExpiryEmail(data: RegistrationExpiryEmailData): Promise<void> {
  const urgency  = data.daysUntilExpiry <= 7 ? "urgent" : "warning";
  const bgColor  = urgency === "urgent" ? "#fee2e2" : "#fef3c7";
  const bdColor  = urgency === "urgent" ? "#fca5a5" : "#fde68a";
  const expDate  = new Date(data.expiryDate).toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const content = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background-color:${bgColor};border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <circle cx="12" cy="12" r="10" stroke="#92400e" stroke-width="2"/>
          <line x1="12" y1="8" x2="12" y2="12" stroke="#92400e" stroke-width="2" stroke-linecap="round"/>
          <line x1="12" y1="16" x2="12.01" y2="16" stroke="#92400e" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      Registration Expiring Soon
    </h1>
    <p style="margin:0 0 28px;font-size:15px;color:#475569;text-align:center;">
      Unit ${data.unitNumber} · ${data.orgName}
    </p>

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      Hi <strong>${data.hostName}</strong>,
    </p>
    <p style="margin:0 0 20px;font-size:15px;color:#334155;line-height:1.6;">
      Your Short-Term Rental registration for <strong>Unit ${data.unitNumber}</strong> is expiring in
      <strong>${data.daysUntilExpiry} day${data.daysUntilExpiry !== 1 ? "s" : ""}</strong>.
      Please renew before the expiry date to avoid interruption to your bookings.
    </p>

    ${detailsCard([
      { label: "🏠  Unit",       value: `Unit ${data.unitNumber} — ${data.orgName}` },
      { label: "📅  Expires",    value: expDate },
      { label: "⏳  Time left",  value: `${data.daysUntilExpiry} day${data.daysUntilExpiry !== 1 ? "s" : ""}` },
    ])}

    ${infoBox("⚠️", "Once your registration expires, new bookings will be blocked until you renew.", bgColor, bdColor)}

    ${ctaButton("Renew Registration", data.renewalUrl)}

    ${divider()}
    <p style="margin:0;font-size:13px;color:#94a3b8;">
      Contact your property manager if you need assistance with the renewal process.
    </p>
  `;

  await sendEmail({
    to: data.hostEmail,
    subject: `⚠️ STR Registration expires in ${data.daysUntilExpiry} days — Unit ${data.unitNumber}`,
    html: emailShell(content, `Your STR registration for Unit ${data.unitNumber} expires in ${data.daysUntilExpiry} days`),
  });
}

// ─── Template 6: Compliance Action Issued ────────────────────────────────────

interface ComplianceActionEmailData {
  hostEmail: string;
  hostName: string;
  unitNumber: string;
  orgName: string;
  actionType: string; // notice | warning | penalty | order | reminder
  description: string;
  issuedBy: string;
  dashboardUrl: string;
}

export async function sendComplianceActionEmail(data: ComplianceActionEmailData): Promise<void> {
  const isSevere   = data.actionType === "penalty" || data.actionType === "order";
  const typeLabel  = data.actionType.charAt(0).toUpperCase() + data.actionType.slice(1);
  const bgColor    = isSevere ? "#fee2e2" : "#fef3c7";
  const bdColor    = isSevere ? "#fca5a5" : "#fde68a";

  const content = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background-color:${bgColor};border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="#92400e" stroke-width="2" stroke-linecap="round"/>
          <line x1="12" y1="9" x2="12" y2="13" stroke="#92400e" stroke-width="2" stroke-linecap="round"/>
          <line x1="12" y1="17" x2="12.01" y2="17" stroke="#92400e" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      Compliance ${typeLabel} Issued
    </h1>
    <p style="margin:0 0 28px;font-size:15px;color:#475569;text-align:center;">
      Unit ${data.unitNumber} · ${data.orgName}
    </p>

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      Hi <strong>${data.hostName}</strong>,
    </p>
    <p style="margin:0 0 20px;font-size:15px;color:#334155;line-height:1.6;">
      A compliance <strong>${data.actionType}</strong> has been issued for <strong>Unit ${data.unitNumber}</strong> by
      <strong>${data.issuedBy}</strong> at <strong>${data.orgName}</strong>. Please review the details below.
    </p>

    ${detailsCard([
      { label: "📋  Action Type", value: typeLabel },
      { label: "🏠  Unit",        value: `Unit ${data.unitNumber} — ${data.orgName}` },
      { label: "👤  Issued By",   value: data.issuedBy },
    ])}

    ${infoBox("📝", `<strong>Reason:</strong> ${data.description}`, bgColor, bdColor)}

    ${ctaButton("View in Dashboard", data.dashboardUrl)}

    ${divider()}
    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      If you believe this action was issued in error, please contact your property manager to discuss.
      You can also view your full compliance history in the Stratakey dashboard.
    </p>
  `;

  await sendEmail({
    to: data.hostEmail,
    subject: `Compliance ${typeLabel} Issued — Unit ${data.unitNumber}, ${data.orgName}`,
    html: emailShell(content, `A compliance ${data.actionType} has been issued for Unit ${data.unitNumber}`),
  });
}

// ─── Template 7: Dispute Filed (to PM) ────────────────────────────────────────

interface DisputeFiledEmailData {
  pmEmail: string;
  pmName: string;
  disputeId: string;
  hostName: string;
  unitNumber: string;
  orgName: string;
  reason: string;
  disputeType?: string;
  priority?: string;
  dashboardUrl: string;
}

export async function sendDisputeFiledEmail(data: DisputeFiledEmailData): Promise<void> {
  const priorityColors: Record<string, string> = {
    urgent: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#16a34a",
  };
  const priorityLabel = data.priority ? data.priority.charAt(0).toUpperCase() + data.priority.slice(1) : "Medium";
  const priorityColor = priorityColors[data.priority ?? "medium"] ?? "#64748b";

  const content = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background-color:#fef3c7;border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#92400e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      New Dispute Filed
    </h1>
    <p style="margin:0 0 28px;font-size:15px;color:#475569;text-align:center;">
      ${data.orgName} · Requires your review
    </p>

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      Hi <strong>${data.pmName}</strong>,
    </p>
    <p style="margin:0 0 20px;font-size:15px;color:#334155;line-height:1.6;">
      A new dispute has been filed by <strong>${data.hostName}</strong> regarding <strong>Unit ${data.unitNumber}</strong>.
      Please review and take appropriate action.
    </p>

    ${detailsCard([
      { label: "🏠  Unit",         value: `Unit ${data.unitNumber} — ${data.orgName}` },
      { label: "👤  Filed By",     value: data.hostName },
      { label: "📋  Type",         value: data.disputeType ? data.disputeType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "General" },
      { label: "🔴  Priority",     value: `<span style="color:${priorityColor};font-weight:700;">${priorityLabel}</span>` },
      { label: "🆔  Dispute ID",   value: `#${data.disputeId.slice(0, 8).toUpperCase()}` },
    ])}

    ${infoBox("📝", `<strong>Reason:</strong> ${data.reason}`, "#fef3c7", "#fde68a")}

    ${ctaButton("Review Dispute", data.dashboardUrl)}

    ${divider()}
    <p style="margin:0;font-size:13px;color:#94a3b8;">
      You're receiving this as a property manager for ${data.orgName} on Stratakey.
    </p>
  `;

  await sendEmail({
    to: data.pmEmail,
    subject: `New Dispute Filed — Unit ${data.unitNumber}, ${data.orgName} [${priorityLabel} Priority]`,
    html: emailShell(content, `${data.hostName} filed a dispute regarding Unit ${data.unitNumber}`),
  });
}

// ─── Template 8: Dispute Resolved (to Host) ──────────────────────────────────

interface DisputeResolvedEmailData {
  hostEmail: string;
  hostName: string;
  disputeId: string;
  orgName: string;
  resolutionAction?: string;
  resolution?: string;
  dashboardUrl: string;
}

export async function sendDisputeResolvedEmail(data: DisputeResolvedEmailData): Promise<void> {
  const actionLabel = data.resolutionAction
    ? data.resolutionAction.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Resolved";

  const content = `
    <div style="text-align:center;margin-bottom:28px;">
      <div style="display:inline-block;background-color:#ede9fe;border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
          <polyline points="22 4 12 14.01 9 11.01" stroke="#4f46e5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      Dispute Resolved
    </h1>
    <p style="margin:0 0 28px;font-size:15px;color:#475569;text-align:center;">
      ${data.orgName} · Dispute #${data.disputeId.slice(0, 8).toUpperCase()}
    </p>

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      Hi <strong>${data.hostName}</strong>,
    </p>
    <p style="margin:0 0 20px;font-size:15px;color:#334155;line-height:1.6;">
      Your dispute has been reviewed and resolved by the property management team at
      <strong>${data.orgName}</strong>.
    </p>

    ${detailsCard([
      { label: "🆔  Dispute ID",    value: `#${data.disputeId.slice(0, 8).toUpperCase()}` },
      { label: "⚖️  Decision",      value: `<strong>${actionLabel}</strong>` },
    ])}

    ${data.resolution ? infoBox("📋", `<strong>Resolution note:</strong> ${data.resolution}`, "#eff6ff", "#bfdbfe") : ""}

    ${ctaButton("View Dispute Details", data.dashboardUrl)}

    ${divider()}
    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      If you have further questions about this decision, please contact your property manager directly.
    </p>
  `;

  await sendEmail({
    to: data.hostEmail,
    subject: `Your dispute has been resolved — ${data.orgName}`,
    html: emailShell(content, `Your dispute #${data.disputeId.slice(0, 8)} at ${data.orgName} has been resolved`),
  });
}

// ─── Template 9: Host Invitation ─────────────────────────────────────────────

interface HostInvitationEmailData {
  inviteeEmail: string;
  orgName: string;
  unitNumber: string;
  inviterName: string;
  acceptUrl: string;
  expiresAt: string;
  nightLimit: number;
}

export async function sendHostInvitationEmail(data: HostInvitationEmailData): Promise<void> {
  const expiryDate = new Date(data.expiresAt).toLocaleDateString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const content = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background-color:#dcfce7;border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <polyline points="9 22 9 12 15 12 15 22" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>
    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      You're invited to register as a host
    </h1>
    <p style="margin:0 0 24px;font-size:16px;color:#475569;text-align:center;">
      <strong style="color:#0f172a;">${data.orgName}</strong> — Unit ${data.unitNumber}
    </p>
    ${divider()}
    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      <strong>${data.inviterName}</strong> has invited you to register as a Short-Term Rental host for
      <strong>Unit ${data.unitNumber}</strong> at <strong>${data.orgName}</strong> on the Stratakey STR Compliance Platform.
    </p>
    <p style="margin:0 0 8px;font-size:14px;color:#475569;line-height:1.6;">As a registered host, you'll be able to:</p>
    <ul style="margin:8px 0 20px;padding-left:20px;font-size:14px;color:#475569;line-height:1.8;">
      <li>Manage your STR bookings and guest check-ins</li>
      <li>Upload and track compliance documents</li>
      <li>View your night usage against your annual limit</li>
      <li>Receive notifications about your registration status</li>
    </ul>
    ${detailsCard([
      { label: "🏠  Unit",           value: `Unit ${data.unitNumber} — ${data.orgName}` },
      { label: "🌙  Night Limit",    value: `${data.nightLimit} nights / year` },
      { label: "⏳  Invite Expires", value: expiryDate },
    ])}
    ${ctaButton("Accept Host Invitation", data.acceptUrl)}
    ${infoBox(
      "ℹ️",
      "Once you accept, your registration will be reviewed by the property manager. You will be notified when your status is updated.",
      "#eff6ff",
      "#bfdbfe"
    )}
    ${divider()}
    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      If you weren't expecting this invitation, you can safely ignore this email.
      <br/><br/>
      Questions? Contact <a href="mailto:support@stratakey.com" style="color:#4f46e5;text-decoration:none;">support@stratakey.com</a>.
    </p>
  `;

  await sendEmail({
    to: data.inviteeEmail,
    subject: `You're invited to register as a host at ${data.orgName} — Unit ${data.unitNumber}`,
    html: emailShell(
      content,
      `${data.inviterName} has invited you to register as an STR host at Unit ${data.unitNumber}, ${data.orgName}`
    ),
  });
}

// ─── Template 10: Universal Invitation (host / individual inviter) ────────────

interface UniversalInvitationEmailData {
  inviteeEmail: string;
  role: string;           // InvitationRole
  inviterName: string;
  orgName?: string;       // optional — may not exist for personal invitations
  acceptUrl: string;
  expiresAt: string;      // ISO string
}

export async function sendUniversalInvitationEmail(data: UniversalInvitationEmailData): Promise<void> {
  const roleLabels: Record<string, string> = {
    strata_admin:     "Strata Admin / Council Member",
    property_manager: "Property Manager",
    host:             "Host",
    individual:       "Individual Property Owner",
  };
  const roleLabel  = roleLabels[data.role] ?? data.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const expiryDate = new Date(data.expiresAt).toLocaleDateString("en-CA", {
    weekday: "long",
    month:   "long",
    day:     "numeric",
    year:    "numeric",
  });

  const roleCapabilities: Record<string, string[]> = {
    host: [
      "Manage STR bookings and guest check-ins",
      "Upload and track compliance documents",
      "View night usage against your annual limit",
      "Submit maintenance and service requests",
    ],
    individual: [
      "Manage your properties and bookings",
      "Track income, expenses, and night usage",
      "Upload compliance documents",
      "Access maintenance and dispute tools",
    ],
    property_manager: [
      "Manage hosts, units, and compliance",
      "Review and approve bookings",
      "Access reports and audit logs",
      "Send announcements and messages",
    ],
    strata_admin: [
      "Configure strata rules and STR policies",
      "Monitor all units and host registrations",
      "Manage the compliance workflow",
      "Access full audit and reporting tools",
    ],
  };

  const capabilities = roleCapabilities[data.role] ?? [
    "Access your Stratakey dashboard",
    "Manage properties and compliance",
  ];

  const orgLine = data.orgName
    ? `<p style="margin:0 0 20px;font-size:15px;color:#334155;line-height:1.6;">
        <strong>${data.inviterName}</strong> has invited you to join
        <strong>${data.orgName}</strong> on the Stratakey STR Compliance Platform as a
        <strong>${roleLabel}</strong>.
      </p>`
    : `<p style="margin:0 0 20px;font-size:15px;color:#334155;line-height:1.6;">
        <strong>${data.inviterName}</strong> has invited you to register on the Stratakey
        STR Compliance Platform as a <strong>${roleLabel}</strong>.
      </p>`;

  const content = `
    <!-- Icon -->
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background-color:#ede9fe;border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
          <circle cx="9" cy="7" r="4" stroke="#4f46e5" stroke-width="2"/>
          <line x1="19" y1="8" x2="19" y2="14" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
          <line x1="22" y1="11" x2="16" y2="11" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      You've been invited to Stratakey
    </h1>
    <p style="margin:0 0 24px;font-size:15px;color:#475569;text-align:center;">
      Join as a <strong style="color:#4f46e5;">${roleLabel}</strong>${data.orgName ? ` · ${data.orgName}` : ""}
    </p>

    ${divider()}

    ${orgLine}

    <p style="margin:0 0 8px;font-size:14px;color:#475569;line-height:1.6;">As a ${roleLabel}, you'll be able to:</p>
    <ul style="margin:8px 0 20px;padding-left:20px;font-size:14px;color:#475569;line-height:1.8;">
      ${capabilities.map((c) => `<li>${c}</li>`).join("")}
    </ul>

    ${ctaButton("Accept Invitation & Register", data.acceptUrl)}

    ${detailsCard([
      { label: "👤  Invited by",  value: data.inviterName },
      { label: "🏷️  Your role",   value: roleLabel },
      ...(data.orgName ? [{ label: "🏢  Organization", value: data.orgName }] : []),
      { label: "⏳  Expires",     value: expiryDate },
    ])}

    ${infoBox(
      "ℹ️",
      "If you already have a Stratakey account, simply sign in after clicking the link above — your invitation will be applied automatically.",
      "#eff6ff",
      "#bfdbfe"
    )}

    ${divider()}

    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      If you weren't expecting this invitation, you can safely ignore this email.
      The link expires automatically and no action is required.
      <br/><br/>
      Questions? Contact <a href="mailto:support@stratakey.com" style="color:#4f46e5;text-decoration:none;">support@stratakey.com</a>.
    </p>
  `;

  await sendEmail({
    to: data.inviteeEmail,
    subject: `${data.inviterName} invited you to join Stratakey as a ${roleLabel}`,
    html: emailShell(
      content,
      `${data.inviterName} has invited you to join Stratakey as a ${roleLabel}${data.orgName ? ` at ${data.orgName}` : ""}`
    ),
  });
}

// ─── Template 3: Booking Approved (Host notification) — implementation ───────

export async function sendBookingApprovedEmail(data: BookingApprovedEmailData): Promise<void> {
  const fmt = (d: string) =>
    new Date(d + "T12:00:00").toLocaleDateString("en-CA", {
      weekday: "short",
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  const content = `
    <!-- Icon -->
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background-color:#ede9fe;border-radius:50%;padding:16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="#4f46e5" stroke-width="2"/>
          <line x1="16" y1="2" x2="16" y2="6" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
          <line x1="8"  y1="2" x2="8"  y2="6" stroke="#4f46e5" stroke-width="2" stroke-linecap="round"/>
          <line x1="3"  y1="10" x2="21" y2="10" stroke="#4f46e5" stroke-width="2"/>
        </svg>
      </div>
    </div>

    <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;text-align:center;letter-spacing:-0.5px;">
      Booking Approved
    </h1>
    <p style="margin:0 0 28px;font-size:15px;color:#475569;text-align:center;">
      A booking for your unit has been approved
    </p>

    <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
      Hi <strong>${data.hostName}</strong>,<br/>
      A new booking for <strong>Unit ${data.unitNumber}</strong> has been reviewed and approved.
      Your guest will receive a separate confirmation with check-in instructions.
    </p>

    ${detailsCard([
      { label: "👤  Guest",     value: data.guestName + (data.guestEmail ? `<br/><span style="font-size:12px;font-weight:400;color:#64748b;">${data.guestEmail}</span>` : "") },
      { label: "🏠  Unit",      value: `Unit ${data.unitNumber} — ${data.orgName}` },
      { label: "📅  Check-in",  value: fmt(data.checkIn) },
      { label: "📅  Check-out", value: fmt(data.checkOut) },
      { label: "🌙  Duration",  value: `${data.nights} night${data.nights !== 1 ? "s" : ""}` },
      { label: "👥  Guests",    value: String(data.guestCount) },
    ])}

    ${ctaButton("View in Dashboard", data.dashboardUrl)}

    ${infoBox(
      "ℹ️",
      "Your guest has been sent a confirmation email with their online check-in link. They must acknowledge building rules and upload their ID before arrival.",
      "#eff6ff",
      "#bfdbfe"
    )}

    ${divider()}

    <p style="margin:0;font-size:13px;color:#94a3b8;line-height:1.6;">
      You're receiving this because a booking was approved for a unit you manage on Stratakey.
    </p>
  `;

  await sendEmail({
    to: data.hostEmail,
    subject: `Booking approved: ${data.guestName} checking in ${fmt(data.checkIn)} — Unit ${data.unitNumber}`,
    html: emailShell(
      content,
      `${data.guestName} has a confirmed booking at Unit ${data.unitNumber} from ${fmt(data.checkIn)} to ${fmt(data.checkOut)}`
    ),
    bookingId: data.bookingId,
  });
}
